import React, { useState, useRef, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"

import Header from "./Components/Header";
import Sidebar from "./Components/Sidebar/Sidebar";
import NavTabs from "./Components/NavTabs/NavTabs";

import Prescriptions from "./Components/NavTabs/Pharmacy/Prescriptions";
import Appointments from "./Components/NavTabs/Outpatient/Appointments";
import Admissions from "./Components/NavTabs/Inpatient/Admissions";
import Dashboard from "./Components/NavTabs/Outpatient/Dashboard";
import InPatientDashboard from "./Components/NavTabs/Inpatient/Dashboard";
import PharmacyDashboard from "./Components/NavTabs/Pharmacy/Dashboard";
import NewAppointment from "./Components/NavTabs/Outpatient/NewAppointment";
// Pharmacy Suppliers Management Component (renders suppliers list, metrics, and filter pills)
import Suppliers from "./Components/NavTabs/Pharmacy/Suppliers/Suppliers";
// Pharmacy Supplier Payment Component (matching reference Pic 2)
import PharmacySupplierPayment from "./Components/NavTabs/Pharmacy/Suppliers/PharmacySupplierPayment";
// Pharmacy Purchase Entries Component (matching reference design & IP screen design)
import PurchaseEntries from "./Components/NavTabs/Pharmacy/PurchaseEntries/PurchaseEntries";
// Pharmacy Items Master Catalog Component (matching reference design)
import Items from "./Components/NavTabs/Pharmacy/Items/Items";


import AiChatWidget from "./Components/AiAssistant/AiChatWidget";
import "./App.css";
import { ToastContainer } from "react-toastify";
import Patients from "./Components/NavTabs/Outpatient/Patients/Patients";
import InPatients from "./Components/NavTabs/Inpatient/Patients/Patients";
import LoginPage from "./Components/UserAccounts/LoginPage";
import PackageManagement from "./Components/NavTabs/Administration/Packages/PackageManagement";
import { useAuth } from "./Components/UserAccounts/AuthProvider";
import PatientInfoDisplay from "./Components/NavTabs/Outpatient/Patients/PatientsInfoDisplay/PatientInfoDisplay";
import PaymentReport from "./Components/NavTabs/Outpatient/Report/Payments/PaymentReport";
import CollectionReport from "./Components/NavTabs/Outpatient/Report/Collection/CollectionReport";
import AppointmentReport from "./Components/NavTabs/Outpatient/Report/AppointmentReport/AppointmentReport";
import OpChargesReport from "./Components/NavTabs/Outpatient/Report/OpChargesReport/OpChargesReport";
import InpatientManage from "./Components/NavTabs/Inpatient/Manage/InpatientManage";

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sidebarRef = useRef(null);

  // const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isAuthenticated, loading, login, logout } = useAuth();
  const location = useLocation();

  const handleClickOutside = (e) => {
    if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
      setSidebarOpen(true);
    }
  };

  useEffect(() => {
    if (sidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);

  return (
    <>
      <ToastContainer position="top-center" autoClose={2000} />
      {/* <Layout
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarRef={sidebarRef}
      /> */}

      {/* If not authenticated, show only login page */}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <Routes>
          {/* Public Route: Login */}
          <Route path="/login" element={<LoginPage onLogin={login} />} />

          {/* Protected Routes */}
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <Layout
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                  sidebarRef={sidebarRef}
                />
              ) : (
                // <Navigate to="/login" replace />
                <Navigate to="/login" replace state={{ from: location }} />
              )
            }
          />
        </Routes>)}

      <Analytics />
      <SpeedInsights />
    </>
  );
}

// Separate Layout component to use hooks like useNavigate & useLocation
function Layout({ sidebarOpen, setSidebarOpen, sidebarRef }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract active tab and sidebar item from current URL
  // URLs format: /tab/sidebarItem, e.g. /outpatient/dashboard
  const pathParts = location.pathname.split("/").filter(Boolean); // removes empty string
  const activeTab = (pathParts[0] || "outpatient").toUpperCase(); // default outpatient
  const selectedSidebarItem = (pathParts[1] || "dashboard").toUpperCase().replace(/-/g, ' ');

  // Handle tab click from NavTabs - navigate to default sidebar item (dashboard)
  const handleTabClick = (tab) => {
    const lowerTab = tab.toLowerCase();
    navigate(`/${lowerTab}/dashboard`);
  };

  // Handle sidebar item click - navigate to corresponding path
  const handleSidebarItemClick = (item) => {
    const lowerTab = activeTab.toLowerCase();
    const lowerItem = item.toLowerCase().replace(/\s+/g, '-');
    navigate(`/${lowerTab}/${lowerItem}`);
  };

  return (
    <>
      <Header onToggleMenu={() => setSidebarOpen((prev) => !prev)} />
      <NavTabs activeTab={activeTab} onTabClick={handleTabClick} />
      <div ref={sidebarRef}>
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeTab={activeTab}
          selectedItem={selectedSidebarItem}
          onItemClick={handleSidebarItemClick}
        />
      </div>

      <AiChatWidget />

      <div className={`main-content ${sidebarOpen ? "shifted" : ""}`}>
        <Routes>
          {/* Redirect root to default */}
          <Route path="/" element={<Navigate to="/outpatient/dashboard" replace />} />

          {/* OUTPATIENT */}
          <Route path="/outpatient/dashboard" element={<Dashboard />} />
          <Route path="/outpatient/appointments" element={<Appointments />} />
          <Route
            path="/outpatient/appointments/newAppointment"
            element={<NewAppointment onBack={() => window.history.back()} />}
          />
          <Route
            path="/outpatient/appointments/patientInfoPanel/:id"
            element={<Appointments />}
          />

          <Route path="/outpatient/patients" element={<Patients />} />

          <Route
            path="/outpatient/patients/patientInfoDisplay/:id"
            element={<Patients />}
          />

          <Route
            path="/outpatient/manage"
            element={<h2 style={{ padding: "20px" }}>MANAGE Under Development</h2>}
          />

          <Route
            path="/outpatient/report/payments"
            element={<PaymentReport />}
          />

          <Route
            path="/outpatient/report/collection"
            element={<CollectionReport />}
          />

          <Route
            path="/outpatient/report/appointment"
            element={<AppointmentReport />}
          />
          <Route
            path="/outpatient/report/op-charges"
            element={<OpChargesReport />}
          />

          {/* PHARMACY */}
          <Route
            path="/pharmacy/dashboard"
            element={<PharmacyDashboard />}
          />
          <Route path="/pharmacy/prescriptions" element={<Prescriptions />} />
          <Route
            path="/pharmacy/prescriptions/patientInfoPanel/:id"
            element={<Prescriptions />}
          />
          <Route
            path="/pharmacy/prescriptions/newPrescription"
            element={<Prescriptions />}
          />
          <Route
            path="/pharmacy/items"
            element={<Items />}
          />
          {/* Pharmacy Suppliers Route: Displays suppliers metrics, column filter pills, and suppliers table */}
          <Route
            path="/pharmacy/suppliers"
            element={<Suppliers />}
          />
          {/* Pharmacy Supplier Payment Route: Dedicated payment & purchase entries screen matching Pic 2 */}
          <Route
            path="/pharmacy/pharmacySupplierPayment"
            element={<PharmacySupplierPayment />}
          />
          <Route
            path="/pharmacy/suppliers/payments/:supplierId?"
            element={<PharmacySupplierPayment />}
          />
          {/* Pharmacy Purchase Entries Route: Clean hyphenated URL & PO Details Path */}
          <Route
            path="/pharmacy/purchase-entries"
            element={<PurchaseEntries />}
          />
          <Route
            path="/pharmacy/purchase-entries/:orderId"
            element={<PurchaseEntries />}
          />
          <Route
            path="/pharmacy/purchase-order/:orderId"
            element={<PurchaseEntries />}
          />
          <Route
            path="/pharmacy/purchase entries"
            element={<Navigate to="/pharmacy/purchase-entries" replace />}
          />
          <Route
            path="/pharmacy/purchase entries/:orderId"
            element={<PurchaseEntries />}
          />

          {/* INPATIENT */}
          <Route
            path="/inpatient/dashboard"
            element={<InPatientDashboard />}
          />
          <Route path="/inpatient/admissions" element={<Admissions />} />

          <Route path="/inpatient/admissions/newAdmission" element={<Admissions />} />
          <Route
            path="/inpatient/admissions/patientInfoPanel/:id"
            element={<Admissions />}
          />


          <Route path="/inpatient/patients" element={<InPatients />} />
          <Route
            path="/inpatient/patients/patientInfoPanel/:id"
            element={<InPatients />}
          />
          <Route
            path="/inpatient/patients/patientInfoDisplay/:id"
            element={<InPatients />}
          />
          <Route
            path="/inpatient/manage"
            element={<InpatientManage defaultTab="rooms" />}
          />
          <Route
            path="/inpatient/manage/:subTab"
            element={<InpatientManage />}
          />

          {/* CENTRAL BILLING */}
          <Route
            path="/central billing/:item?"
            element={<CentralBillingPage />}
          />

          {/* ADMINISTRATION */}
          <Route
            path="/administration/*"
            element={<AdministrationPage />}
          />

          {/* Fallback */}
          <Route
            path="*"
            element={<h2 style={{ padding: "20px" }}>Page Not Found</h2>}
          />
        </Routes>
      </div>
    </>
  );
}

// Placeholder components for Central Billing and Administration
const CentralBillingPage = () => {
  const location = useLocation();
  const selectedItem = location.pathname.split("/").pop().replace(/-/g, " ").toUpperCase();
  return (
    <h2 style={{ padding: "20px" }}>
      {selectedItem} (Central Billing)
    </h2>
  );
};

const AdministrationPage = () => {
  const location = useLocation();
  const fullPath = decodeURIComponent(location.pathname).toUpperCase();
  const rawItem = location.pathname.split("/").pop();
  const selectedItem = rawItem ? decodeURIComponent(rawItem).replace(/-/g, " ").toUpperCase() : "";

  if (fullPath.includes("PACKAGE") || fullPath.includes("PACKAGES")) {
    return <PackageManagement />;
  }

  return (
    <h2 style={{ padding: "20px" }}>
      {selectedItem || "Dashboard"} (Administration)
    </h2>
  );
};

export default App;
